import { createContext, useCallback, useContext, useMemo, useState } from 'react'

const STORAGE_KEY = 'listings.preferences'

const defaultPreferences = {
  viewMode: 'cards',
  scrollPosition: 0,
}

const readStoredPreferences = () => {
  if (typeof window === 'undefined') {
    return defaultPreferences
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return defaultPreferences
    const parsed = JSON.parse(stored)
    if (typeof parsed !== 'object' || parsed === null) {
      return defaultPreferences
    }
    return {
      ...defaultPreferences,
      ...parsed,
    }
  } catch {
    return defaultPreferences
  }
}

const ListingsPreferencesContext = createContext({
  preferences: defaultPreferences,
  setViewMode: () => {},
  setScrollPosition: () => {},
})

export const ListingsPreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(() => readStoredPreferences())

  const persist = useCallback((nextPrefs) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextPrefs))
    } catch {
      // Ignore write errors (e.g. private mode)
    }
    return nextPrefs
  }, [])

  const setViewMode = useCallback(
    (mode) => {
      setPreferences((prev) => persist({ ...prev, viewMode: mode }))
    },
    [persist],
  )

  const setScrollPosition = useCallback(
    (position) => {
      setPreferences((prev) => {
        if (Math.abs(prev.scrollPosition - position) < 2) {
          return prev
        }
        return persist({ ...prev, scrollPosition: position })
      })
    },
    [persist],
  )

  const value = useMemo(
    () => ({
      preferences,
      setViewMode,
      setScrollPosition,
    }),
    [preferences, setViewMode, setScrollPosition],
  )

  return <ListingsPreferencesContext.Provider value={value}>{children}</ListingsPreferencesContext.Provider>
}

export const useListingsPreferences = () => useContext(ListingsPreferencesContext)
